import { JSX, ReactNode, useEffect, useState } from "react";

import { useRootStore } from "store";
import Modal from "components/modal/Modal";
import { Input, Label } from "components/input/Input";
import Link from "components/link/Link";
import { Button, PrimaryButton } from "components/button/Button";
import { Alert } from "components/alert/Alert";
import { initializeExternalMsal, initializeInternalMsal, scopes } from "common/msalInstances";
import { AuthenticationResult, BrowserAuthError, PublicClientApplication } from "@azure/msal-browser";
import { Eye24Regular, EyeOff24Regular } from "@fluentui/react-icons";

import "./LoginForm.scss";

export default function LoginForm() {
  const { supportContactInformation: contactInfo } = useRootStore(
    (state) => state.global.staticInformation
  );
  const { user, error } = useRootStore((state) => state);

  const { appendErrorNotification } = error;

  const { fetchUserLogin, fetchInternalUserLoginEntraAccessToken, fetchExternalUserLoginEntraAccessToken, reset } = user;

  const [showModal, setShowModal] = useState(false);
  const [username, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginErrorMessage, setLoginErrorMessage] = useState("");

  useEffect(() => {
    if (user.showNotActivatedMessage) {
      setShowModal(true);
    }
  }, [user.showNotActivatedMessage]);

  const clearErrors = () => {
    setLoginErrorMessage("");
    reset();
  }

  const loginUser = () => {
    clearErrors();
    fetchUserLogin({
      code: "",
      username: username,
      password: password
    });
  };

  const loginInternalUser = async () => {
    clearErrors();
    const msalInstance = await initializeInternalMsal();
    const {success, result} = await handleMsalPopupLogin(msalInstance);
    if (success && result) {
      try {
        fetchInternalUserLoginEntraAccessToken(result.accessToken);
      } catch (ex) {
        appendErrorNotification("Inloggning misslyckades.", ex);
      }
    }
  };

  const handleMsalPopupLogin = async (msalInstance: PublicClientApplication):
    Promise<{ success: boolean; result?: AuthenticationResult }> => {
    // Clear any previous login attempts to avoid conflicts
    localStorage.removeItem('msal.interaction.status');
    sessionStorage.removeItem('msal.interaction.status');
    try {
      const authenticationResult = await msalInstance.loginPopup({
        scopes: scopes(msalInstance),
        redirectUri: window.location.origin,
      });
      return { success: true, result: authenticationResult };
    } catch (err) {
      if (err instanceof BrowserAuthError) {
        if (err.errorCode === "user_cancelled") {
          setLoginErrorMessage("Inloggning via Entra ID avbröts av användaren.");
        } else if (err.errorCode === "popup_window_error") {
          setLoginErrorMessage("Ett fel uppstod med popup-fönstret. Kontrollera att popup-fönster inte blockeras.");
        }
        else {
          setLoginErrorMessage("Inloggning misslyckades.");
        }
      } else {
        appendErrorNotification("Inloggning misslyckades.", err);
        setLoginErrorMessage("Inloggning misslyckades.");
      }
      return { success: false };
    }
  };

  const loginExternalUserEntra = async () => {
    clearErrors();
    const msalInstance = await initializeExternalMsal();
    const {success, result} = await handleMsalPopupLogin(msalInstance);
    if (success && result) {
      try {
        fetchExternalUserLoginEntraAccessToken(result.accessToken);
      } catch (ex) {
        appendErrorNotification("Inloggning misslyckades.", ex);
      }
    }
  }

  const renderText = (header: string, paragraphs: string[]) => {
    const ps: ReactNode[] = [];
    for (let i = 0; i < paragraphs.length; i++) {
      ps.push(<p key={i}>{paragraphs[i]}</p>);
    }
    return [<h1 key="modH">{header}</h1>, ...ps];
  };

  const renderModal = () => {
    if (!showModal) {
      return;
    }

    return (
      <Modal
        modalTitle="activate account"
        onDismiss={() => setShowModal(false)}
      >
        <div className="modalText">
          {renderText("Aktivera ditt konto", [
            "Du har inte aktiverat ditt konto. Vi har skickat ett nytt aktiveringsmail till din e-postadress.",
            "Får du inget mail? Titta i skräpposten."
          ])}
          <PrimaryButton autoFocus onClick={() => setShowModal(false)}>
            Ok
          </PrimaryButton>
        </div>
      </Modal>
    );
  };

  const renderUsernamePasswordErrors = () => {
    let alerts: JSX.Element[] = [];
    if (user.loginFailed) {
      const wrongPasswordAlert = (
        <span
          className="validationError mb-4"
          key={"wrongPasswordAlert"}
        >
          Fel e-postadress eller lösenord.
        </span>
      );
      const problemsAlert = (
        <Alert
          variant="warning"
          className="mb-4"
          key={"problemsAlert"}
          onDismiss={() => { }}
        >
          <p>
            Har du problem med att logga in? Läs mer under{" "}
            <a
              className="link"
              rel="noreferrer"
              target="_blank"
              href={contactInfo.externalSupportSite}
            >
              Frågor och svar
            </a>{" "}
            om du behöver hjälp.
          </p>
        </Alert>
      );
      alerts = [wrongPasswordAlert, problemsAlert];
    }

    if (user.loginTimeout) {
      alerts.push(
        <Alert
          variant="warning"
          className="mb-4"
          key={"loginTimeoutAlert"}
          onDismiss={() => { }}
        >
          Din session är inte längre giltig, var god logga in igen
        </Alert>
      );
    }

    if (loginErrorMessage) {
      alerts.push(
        <Alert
          variant="warning"
          className="mb-4"
          key={"loginErrorMessageAlert"}
          onDismiss={() => { }}
        >
          {loginErrorMessage}
        </Alert>
      );
    }

    return alerts;
  };

  const renderEntraErrors = () => {
    const alerts: JSX.Element[] = [];
    if (user.noMatchingEntraUserInformation) {
      const { tenantId, objectId } = user.noMatchingEntraUserInformation;
      alerts.push(
        <Alert
          variant="error"
          className="mb-4 mt-4"
          key={"noMatchingEntraUserAlert"}
          onDismiss={() => { }}
        >
          <p>
            Inget konto matchar din Microsoft-identitet. Din rådgivare behöver lägga till följande information till din användare:
          </p>
              <table
              style={{ cursor: 'pointer' }}
              onClick={() => {
                navigator.clipboard.writeText(`tenant id: ${tenantId}\nobject id: ${objectId}`);
              }}
              title="Klicka för att kopiera både Tenant ID och Object ID"
              >
              <tbody>
                <tr>
                <td>Tenant ID:</td>
                <td>{tenantId}</td>
                </tr>
                <tr>
                <td>Object ID:</td>
                <td>{objectId}</td>
                </tr>
              </tbody>
              </table>
            <p className="text-sm text-muted mt-2">
            Klicka på värdena ovan för att kopiera till urklipp
            </p>
        </Alert>
      );
    }
    return alerts;
  };

  return (
    <div className="loginFormWrapper">
      {renderEntraErrors()}
      <h1>Välkommen tillbaka</h1>
      <div className="textMuted mb-5">
        Logga in med en av metoderna nedan
      </div>
      <form
        className="loginForm"
        onSubmit={(e) => {
          e.preventDefault();
          // Default action is to login with password
          if (username && password) {
            loginUser();
          }
        }}
      >
        {renderModal()}

        <div className="usernamePasswordLoginForm">
          <Label htmlFor="username">E-postadress</Label>
          <Input
            className="mb-3 rounded"
            type="text"
            name="username"
            placeholder="email@email.com"
            value={username}
            autoFocus
            onChange={(e) => {
              setUserName(e.target.value.trim());
            }}
          />

          <Label htmlFor="password">Lösenord</Label>
          <div className="password-input-container">
            <Input
              className="mb-3 rounded"
              type={showPassword ? "text" : "password"}
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="lösenord"
            />
            {password.length > 0 && (
              <>
                <Eye24Regular
                  className={`password-toggle-icon ${showPassword ? "active" : ""}`}
                  visibility={showPassword ? "visible" : "hidden"}
                  onClick={() => setShowPassword(false)}
                />
                <EyeOff24Regular
                  className={`password-toggle-icon ${!showPassword ? "active" : ""}`}
                  visibility={!showPassword ? "visible" : "hidden"}
                  onClick={() => setShowPassword(true)}
                />
              </>
            )}
          </div>

          {renderUsernamePasswordErrors()}

          <PrimaryButton
            size="md"
            disabled={user.isAuthenticating || !username || !password}
            onClick={loginUser}
            type="submit"
            className="mb-5"
          >
            Logga in
          </PrimaryButton>

          <div className="loginFormLinks">
            <Link className="link" to="/resetpassword">
              Glömt lösenord?
            </Link>
            <Link className="link" to="/generate-onetime-login">
              Engångslänk för inloggning
            </Link>
          </div>
        </div>
        <p className="textMuted mt-6 mb-6">
          eller logga in med
        </p>
        <div className="alternativeLoginMethodsForm">
          <Button
            size="md"
            onClick={loginExternalUserEntra}
            type="button"
            className="mb-3"
          >
            Logga in med Microsoft Entra ID 🡢
          </Button>

          <div className="loginFormLinks">
            <a
              className="muted"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                loginInternalUser();
              }}
            >
              Logga in som Ludviganställd
            </a>
          </div>
        </div>
      </form>
    </div>
  );
}

